import React, { useState } from "react";
import Swal from "sweetalert2";
import { defaultRoundContent, MEDIA_ANSWER_TYPES, answerTypeLabel, hintPenaltyTypeFor } from "../constants";
import { generateAi, parseAiJson } from "../../../functions/generateAi";
import { shapeForAnswerType, typeInstructionFor } from "../aiShapes";

function ReviewContent({ hunt, updateHunt, adminToken }) {
  const rounds = hunt.rounds || [];
  const roundContent = hunt.roundContent && hunt.roundContent.length === rounds.length
    ? hunt.roundContent
    : rounds.map((r) => defaultRoundContent(r.id));
  const [selected, setSelected] = useState(0);
  const [regenerating, setRegenerating] = useState(false);

  const current = roundContent[selected] || defaultRoundContent(rounds[selected]?.id);
  const currentRound = rounds[selected];
  const checkpoint = hunt.checkpoints?.[selected % Math.max(hunt.checkpoints.length, 1)];

  const patchCurrent = (patch) => {
    const next = roundContent.map((rc, i) => (i === selected ? { ...rc, ...patch, approved: false } : rc));
    updateHunt({ roundContent: next });
  };

  const validateForApprove = (round, rc) => {
    if (!rc.question.trim()) return "A question is required before approving.";
    if ((round?.answerType === "text" || round?.answerType === "numeric") && !rc.acceptedAnswer.trim()) {
      return "An accepted answer is required before approving.";
    }
    if (round?.answerType === "mcq") {
      const filled = (rc.options || []).filter((o) => o.trim());
      if (filled.length < 2) return "Add at least 2 answer options before approving.";
      if (!rc.options?.[rc.correctOption]?.trim()) return "The option marked correct can't be blank.";
    }
    return null;
  };

  const approve = () => {
    const err = validateForApprove(currentRound, current);
    if (err) {
      Swal.fire("Missing fields", err, "warning");
      return;
    }
    const next = roundContent.map((rc, i) => (i === selected ? { ...rc, approved: true } : rc));
    updateHunt({ roundContent: next });
    // Move straight on to whatever still needs review, instead of leaving the admin sitting on
    // the round they just finished — skips ahead past any round already approved earlier.
    const nextUnapproved = next.findIndex((rc, i) => i > selected && !rc.approved);
    if (nextUnapproved !== -1) setSelected(nextUnapproved);
    else if (selected < rounds.length - 1) setSelected(selected + 1);
  };

  const approveAll = () => {
    const next = [...roundContent];
    const failures = [];
    rounds.forEach((r, i) => {
      const rc = next[i] || defaultRoundContent(r.id);
      const err = validateForApprove(r, rc);
      if (err) {
        failures.push(`Round ${i + 1}: ${err}`);
      } else {
        next[i] = { ...rc, approved: true };
      }
    });
    updateHunt({ roundContent: next });
    if (failures.length) {
      Swal.fire({
        icon: "warning",
        title: `${rounds.length - failures.length} of ${rounds.length} rounds approved`,
        html: `<div style="text-align:left">${failures.map((f) => `<div>${f}</div>`).join("")}</div>`,
      });
    } else {
      Swal.fire({ icon: "success", title: "All rounds approved", timer: 1200, showConfirmButton: false });
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const answerType = currentRound?.answerType;
      const shape = shapeForAnswerType(answerType);
      const typeInstruction = typeInstructionFor(answerType);
      const prompt = [
        `Rewrite the question and clue for round ${selected + 1} of a corporate office treasure hunt.`,
        hunt.contentBrief ? `Brief: ${hunt.contentBrief}` : "",
        `Difficulty: ${hunt.difficulty}. Tone: ${hunt.tone}.`,
        checkpoint?.name ? `This checkpoint is called "${checkpoint.name}".` : "",
        typeInstruction,
        `The "clue" and the "question" serve opposite purposes — never blend them. The clue is a riddle that only leads the team TO this checkpoint's physical location; it must NOT reveal, paraphrase, or make guessable the answer to the question. The question is the actual task, asked only once the team has already arrived.`,
        `Create a short and engaging treasure hunt riddle for the checkpoint. Do not make the clue too obvious — it should feel mysterious and playful while still giving enough hints for players to find the location. Keep it short and easy to understand. Example style: "Pass where letters rest, then turn toward the left of the grand entrance. There, where every visitor is first welcomed, your next clue awaits."`,
        `Reply with ONLY minified JSON: ${shape}`,
      ]
        .filter(Boolean)
        .join("\n");
      const response = await generateAi({
        prompt,
        token: adminToken,
        options: { temperature: 0.9, seed: Math.floor(Math.random() * 1e9) },
      });
      const parsed = parseAiJson(response);
      if (parsed) {
        patchCurrent({
          question: String(parsed.question || "").slice(0, 300),
          clue: String(parsed.clue || "").slice(0, 200),
          hint: String(parsed.hint || ""),
          ...(answerType === "mcq"
            ? {
                options: Array.isArray(parsed.options)
                  ? [0, 1, 2, 3].map((idx) => String(parsed.options[idx] || ""))
                  : ["", "", "", ""],
                correctOption: Math.min(3, Math.max(0, parseInt(parsed.correctOption, 10) || 0)),
                infoMessage: String(parsed.infoMessage || ""),
              }
            : answerType === "numeric"
            ? { acceptedAnswer: String(parsed.acceptedAnswer || ""), infoMessage: String(parsed.infoMessage || "") }
            : answerType === "qrcode" || MEDIA_ANSWER_TYPES.includes(answerType)
            ? {}
            : {
                acceptedAnswer: String(parsed.acceptedAnswer || ""),
                alsoAccept: String(parsed.alsoAccept || ""),
                infoMessage: String(parsed.infoMessage || ""),
              }),
        });
      } else {
        patchCurrent({ question: String(response || "").slice(0, 300) });
      }
    } catch (e) {
      Swal.fire("Error", e.message || "Could not regenerate this clue.", "error");
    } finally {
      setRegenerating(false);
    }
  };

  const allApproved = roundContent.length > 0 && roundContent.every((rc) => rc.approved);

  return (
    <div className="studio-step-page" data-tour="studio-step-review">
      <div className="studio-row-between">
        <div>
          <h2 className="studio-h2">Review content</h2>
          <p className="studio-sub">Every clue, question, answer and hint is editable. Nothing publishes until you approve all {rounds.length} rounds.</p>
        </div>
        <button className="studio-btn studio-btn--approve" onClick={approveAll} disabled={allApproved}>
          {allApproved ? "All rounds approved" : "Approve all"}
        </button>
      </div>

      <div className="studio-review-layout">
        <div className="studio-review-list">
          {rounds.map((r, i) => (
            <div
              key={r.id}
              className={"studio-review-row" + (selected === i ? " studio-review-row--active" : "")}
              onClick={() => setSelected(i)}
            >
              <span className={"studio-gen-dot studio-gen-dot--" + (roundContent[i]?.approved ? "done" : roundContent[i]?.question ? "writing" : "idle")} />
              <div>
                <div className="studio-review-row-title">Round {i + 1}</div>
                <div className="studio-review-row-sub">{hunt.checkpoints?.[i % Math.max(hunt.checkpoints.length, 1)]?.name || "—"}</div>
              </div>
            </div>
          ))}
        </div>

        {currentRound && (
          <div className="studio-review-editor">
            <div className="studio-row-between">
              <div>
                <div className="studio-review-eyebrow">
                  ROUND {selected + 1} · {(checkpoint?.name || "").toUpperCase()}
                </div>
                <div className="studio-review-title">
                  {answerTypeLabel(currentRound.answerType)} · {currentRound.points} points
                </div>
              </div>
              <button className="studio-btn studio-btn--ghost" onClick={regenerate} disabled={regenerating}>
                {regenerating ? "Regenerating..." : "Regenerate round"}
              </button>
            </div>

            <div className="studio-field">
              <label>Clue</label>
              <input
                className="studio-input"
                value={current.clue}
                onChange={(e) => patchCurrent({ clue: e.target.value.slice(0, 200) })}
              />
              <div className="studio-hint">Aim for under 60 characters so it fits two lines on a phone.</div>
            </div>

            <div className="studio-field">
              <label>Question</label>
              <input
                className="studio-input"
                value={current.question}
                onChange={(e) => patchCurrent({ question: e.target.value.slice(0, 300) })}
              />
              <div className="studio-hint">The task or question put to the team at this checkpoint.</div>
            </div>

            {currentRound.answerType === "text" && (
              <div className="studio-two-col">
                <div className="studio-field">
                  <label>Accepted answer</label>
                  <input
                    className="studio-input"
                    value={current.acceptedAnswer}
                    onChange={(e) => patchCurrent({ acceptedAnswer: e.target.value })}
                  />
                </div>
                <div className="studio-field">
                  <label>Also accept</label>
                  <input
                    className="studio-input"
                    value={current.alsoAccept}
                    onChange={(e) => patchCurrent({ alsoAccept: e.target.value })}
                    placeholder="comma, separated, variants"
                  />
                </div>
              </div>
            )}

            {currentRound.answerType === "mcq" && (
              <div className="studio-field">
                <label>Options</label>
                <div className="studio-mcq-options">
                  {[0, 1, 2, 3].map((optIdx) => (
                    <div className="studio-mcq-option-row" key={optIdx}>
                      <input
                        type="radio"
                        name={`review-correct-${currentRound.id}`}
                        checked={current.correctOption === optIdx}
                        onChange={() => patchCurrent({ correctOption: optIdx })}
                        title="Mark as correct"
                      />
                      <input
                        className="studio-input"
                        value={current.options?.[optIdx] || ""}
                        onChange={(e) => {
                          const opts = [...(current.options || ["", "", "", ""])];
                          opts[optIdx] = e.target.value;
                          patchCurrent({ options: opts });
                        }}
                        placeholder={`Option ${optIdx + 1}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="studio-hint">Select the radio next to the correct option.</div>
              </div>
            )}

            {currentRound.answerType === "numeric" && (
              <div className="studio-field">
                <label>Correct number</label>
                <input
                  type="number"
                  className="studio-input"
                  value={current.acceptedAnswer}
                  onChange={(e) => patchCurrent({ acceptedAnswer: e.target.value })}
                />
              </div>
            )}

            {currentRound.answerType === "qrcode" && (
              <div className="studio-hint" style={{ marginBottom: 16 }}>
                Scanning any QR code at this checkpoint is the answer — no text to set here.
              </div>
            )}

            {MEDIA_ANSWER_TYPES.includes(currentRound.answerType) && (
              <div className="studio-hint" style={{ marginBottom: 16 }}>
                Reviewed manually during the hunt — no text answer to set here.
              </div>
            )}

            {["text", "numeric", "mcq"].includes(currentRound.answerType) && (
              <div className="studio-field">
                <label>Info message (optional)</label>
                <input
                  className="studio-input"
                  value={current.infoMessage || ""}
                  onChange={(e) => patchCurrent({ infoMessage: e.target.value })}
                  placeholder="Optional context shown to the team above the question"
                />
              </div>
            )}

            <div className="studio-field">
              <label>Hint</label>
              <input
                className="studio-input"
                value={current.hint}
                onChange={(e) => patchCurrent({ hint: e.target.value })}
              />
              <div className="studio-hint">
                Costs the team {currentRound.hintPenaltyMin}{" "}
                {hintPenaltyTypeFor(hunt.scoringModel) === "points" ? "points" : "minutes"}.
              </div>
            </div>

            <div className="studio-approve-row">
              <button className="studio-btn studio-btn--approve" onClick={approve} disabled={current.approved}>
                {current.approved ? "Round approved" : `Approve round ${selected + 1}`}
              </button>
            </div>
          </div>
        )}
      </div>

      {!allApproved && (
        <div className="studio-hint" style={{ marginTop: 12 }}>
          Approve every round to continue to teams.
        </div>
      )}
    </div>
  );
}

export default ReviewContent;
